import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Paper, Button, Alert, Snackbar, Fade } from '@mui/material';
import { usePdf } from '../../context/PdfContext';
import { getFileType } from '../../utils/docxUtils';

const FileUpload: React.FC = () => {
  const { addFile } = usePdf();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      // Handle accepted files
      if (acceptedFiles.length > 0) {
        let addedCount = 0;
        acceptedFiles.forEach((file) => {
          const fileType = getFileType(file);
          if (fileType === 'pdf' || fileType === 'docx' || fileType === 'xlsx') {
            try {
              addFile(file);
              addedCount++;
            } catch (error) {
              setError(error instanceof Error ? error.message : 'Failed to add file');
              setTimeout(() => setError(null), 5000);
            }
          }
        });
        
        if (addedCount > 0) {
          setSuccess(`Successfully added ${addedCount} document(s)`);
          setTimeout(() => setSuccess(null), 3000);
        }
      }
      
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        setError('Only .pdf, .docx and .xlsx files are supported. Legacy .doc/.xls files are not supported. Please try again.');
        setTimeout(() => setError(null), 5000);
      }
    },
    [addFile]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: true,
    maxSize: 50 * 1024 * 1024, // 50MB max file size
    onDropRejected: (fileRejections) => {
      const sizeError = fileRejections.some(rejection => 
        rejection.errors.some(error => error.code === 'file-too-large')
      );
      
      if (sizeError) {
        setError('File too large. Maximum size is 50MB.');
      }
    }
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        {...getRootProps()}
        sx={{
          p: 2, // Reduced padding
          border: '2px dashed',
          borderColor: isDragReject ? 'error.main' : isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          backgroundColor: isDragReject ? 'rgba(211, 47, 47, 0.04)' : isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'space-between' },
        }}
        role="region"
        aria-label="Document file upload area"
      >
        <input {...getInputProps()} aria-label="Upload PDF and Word documents" />
        
        {/* Left side - Icon and main text */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          mb: { xs: 2, sm: 0 }
        }}>
          <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            {isDragReject ? 'File type not supported' : isDragActive ? 'Drop documents here' : 'Drag & drop .pdf, .docx or .xlsx files here'}
          </Typography>
        </Box>
        
        {/* Right side - Button and file type info */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
            or
          </Typography>
          <Button 
            variant="contained" 
            component="span"
            size="small"
            aria-label="Browse for PDF and Word documents"
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 50%, #64b5f6 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #1e88e5 50%, #42a5f5 100%)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
              },
              '&:active': {
                background: 'linear-gradient(135deg, #0d47a1 0%, #1976d2 50%, #2196f3 100%)',
                boxShadow: '0 2px 3px rgba(0,0,0,0.1)'
              },
              '&:focus': {
                boxShadow: '0 0 0 3px rgba(33, 150, 243, 0.5)'
              }
            }}
          >
            Browse Files
          </Button>
          <Typography variant="caption" color="textSecondary" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
            .pdf, .docx, .xlsx (max 50MB)
          </Typography>
        </Box>
      </Paper>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={5000} 
        onClose={() => setError(null)}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!success} 
        autoHideDuration={3000} 
        onClose={() => setSuccess(null)}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FileUpload;
